/**
 * iter86.41.2 — Template Draft Pots / Tiers editor (Product Ops →
 * Templates). Re-rank a club season template's clubs into tiers 1-4.
 * Tiers drive the Season Pot Pick'em pots (T1→Pot 1, T2→Pot 2,
 * T3+T4→Pot 3) and snake-draft order for normal club seasons.
 * Baseline = prior season's final table; saved tiers apply to NEW
 * tournaments only.
 */
import { useEffect, useState } from "react";
import { Layers, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const LEAGUES = [
    { key: "premier_league_api", label: "Premier League" },
    { key: "la_liga_api", label: "La Liga" },
    { key: "champions_league_api", label: "Champions League" },
    { key: "nfl_season_api", label: "NFL Season" },
];
const TIER_HINTS = { 1: "Pot 1 · contenders", 2: "Pot 2 · chasers", 3: "Pot 3 · mid-table", 4: "Pot 3 · bottom/promoted" };
const NFL_TIER_HINTS = { 1: "Pot 1 · Contenders (SB odds 1–8)", 2: "Pot 2 · Dark horses (9–16)", 3: "Pot 3 · Longshots (17–24)", 4: "Pot 4 · Moonshots (25–32)" };

export default function TemplatePotsCard() {
    const [templateKey, setTemplateKey] = useState("premier_league_api");
    const [data, setData] = useState(null);
    const [rows, setRows] = useState([]);
    const [saving, setSaving] = useState(false);

    const load = (key) => {
        api.siteGetTemplatePots(key)
            .then((d) => { setData(d); setRows(d.teams); })
            .catch(() => toast.error("Failed to load template pots"));
    };
    useEffect(() => { load(templateKey); }, [templateKey]);

    const setSeed = (name, seed) =>
        setRows(rows.map(r => r.name === name ? { ...r, seed: Number(seed) } : r));

    const dirty = data && JSON.stringify(rows) !== JSON.stringify(data.teams);
    const counts = rows.reduce((a, r) => { a[r.seed] = (a[r.seed] || 0) + 1; return a; }, {});
    const isNfl = templateKey === "nfl_season_api";
    const hints = isNfl ? NFL_TIER_HINTS : TIER_HINTS;
    const expected = isNfl ? { 1: 8, 2: 8, 3: 8, 4: 8 } : { 1: 4, 2: 6 };

    const save = async () => {
        setSaving(true);
        try {
            await api.sitePutTemplatePots(templateKey, rows);
            toast.success("Tiers saved — new tournaments will use them");
            load(templateKey);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    return (
        <div className="brutal-card p-5 space-y-4" data-testid="template-pots-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-extrabold text-base uppercase tracking-tight flex items-center gap-2">
                    <Layers className="w-4 h-4 text-cyber" /> Draft pots / tiers
                </h3>
                <select
                    data-testid="template-pots-league-select"
                    value={templateKey}
                    onChange={(e) => setTemplateKey(e.target.value)}
                    className="brutal-input text-xs py-1.5 w-44"
                >
                    {LEAGUES.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                </select>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
                {isNfl
                    ? <>Tier = pot, 8 teams each — Pot 1 Contenders → Pot 4 Moonshots, seeded from <b className="text-zinc-300">preseason Super Bowl odds</b> (favorites first). Pots drive the NFL Pot Pick'em (pick 2 per pot) and the Power Pick multiplier (1× / 1.25× / 1.5× / 2×). Changes apply to <b className="text-cyber">new tournaments only</b>.</>
                    : <>Tier 1 = ranks 1-4 · Tier 2 = 5-10 · Tier 3 = 11-16 · Tier 4 = 17+ / promoted, seeded from
                <b className="text-zinc-300"> last season's final table</b>. Tiers drive Pot Pick'em pots
                (T1→Pot 1 · T2→Pot 2 · T3+T4→Pot 3) and snake-draft order. Changes apply to <b className="text-cyber">new tournaments only</b>.</>}
                {data?.source === "saved" && data?.updated_at && (
                    <span className="block mt-1 text-zinc-600">
                        Custom tiers saved {new Date(data.updated_at).toLocaleString()}{data.updated_by_email ? ` by ${data.updated_by_email}` : ""}.
                    </span>
                )}
            </p>
            <div className="flex gap-2 flex-wrap text-[10px] text-zinc-500">
                {[1, 2, 3, 4].map(t => (
                    <span key={t} className={`pill border py-0.5 ${counts[t] === (expected[t] ?? counts[t]) ? "border-[#333]" : "border-heat text-heat"}`}>
                        T{t}: {counts[t] || 0}
                    </span>
                ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 max-h-80 overflow-y-auto pr-1">
                {rows.map(r => (
                    <div key={r.name} className="flex items-center justify-between gap-2 py-0.5 border-b border-[#222]">
                        <span className="text-xs font-bold truncate">{r.name}</span>
                        <select
                            data-testid={`pot-seed-${r.name.replace(/\s+/g, "-").toLowerCase()}`}
                            value={r.seed}
                            onChange={(e) => setSeed(r.name, e.target.value)}
                            className="brutal-input text-[11px] py-0.5 w-40 shrink-0"
                            title={hints[r.seed]}
                        >
                            {[1, 2, 3, 4].map(t => <option key={t} value={t}>Tier {t} — {hints[t]}</option>)}
                        </select>
                    </div>
                ))}
            </div>
            <button
                data-testid="template-pots-save-btn"
                onClick={save}
                disabled={!dirty || saving}
                className="brutal-btn-primary text-xs py-2 disabled:opacity-50"
            >
                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save tiers"}
            </button>
        </div>
    );
}
